import { useEffect, useRef, useState } from 'react';

interface UseDragToCloseProps {
  isEnabled: boolean;
  onClose?: () => void;
  threshold?: number;
}

interface UseDragToCloseResult {
  dragHandlers: {
    onTouchStart: (e: React.TouchEvent<HTMLDivElement>) => void;
    onTouchMove: (e: React.TouchEvent<HTMLDivElement>) => void;
    onTouchEnd: () => void;
  };
  dragStyles: {
    transform: string | undefined;
    transition: string | undefined;
  };
}

/**
 * A hook that provides drag-to-close functionality for bottom sheets and modals
 * 
 * @param isEnabled Whether the drag functionality is enabled
 * @param onClose Function to call when the drag threshold is exceeded
 * @param threshold Distance in pixels that triggers the close action (default: 150)
 * @returns Object containing drag handlers and styles to apply to the component
 */
export const useDragToClose = ({
  isEnabled,
  onClose,
  threshold = 150,
}: UseDragToCloseProps): UseDragToCloseResult => {
  const [dragOffset, setDragOffset] = useState(0);
  const dragStartY = useRef(0);

  // Reset drag offset when enabled state changes
  useEffect(() => {
    setDragOffset(0);
  }, [isEnabled]);

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isEnabled) return;
    dragStartY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isEnabled) return;
    
    const currentY = e.touches[0].clientY;
    const deltaY = Math.max(0, currentY - dragStartY.current); // Only allow downward drag
    
    setDragOffset(deltaY);
  };

  const handleTouchEnd = () => {
    if (!isEnabled) return;
    
    if (dragOffset > threshold) {
      onClose?.();
    }
    
    // Animate back to original position if not closing
    setDragOffset(0);
  };

  return {
    dragHandlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
    },
    dragStyles: {
      transform: dragOffset > 0 ? `translateY(${dragOffset}px)` : undefined,
      transition: dragOffset > 0 ? 'none' : 'transform 0.3s ease',
    },
  };
};